import { useState } from 'react';
import { Check, X, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { techAssistantAPI } from '@shared/apiClient';

export default function PendingActionCard({ action, onConfirmed, onCancelled }) {
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const { data } = await techAssistantAPI.confirmAction(action.id);
      setStatus('confirmed');
      toast.success('Action completed');
      onConfirmed?.(data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
      setStatus('failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setLoading(true);
    try {
      await techAssistantAPI.cancelAction(action.id);
      setStatus('cancelled');
      onCancelled?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Cancel failed');
    } finally {
      setLoading(false);
    }
  };

  if (status === 'confirmed') {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
        <Check className="h-4 w-4 flex-shrink-0" />
        <span>Confirmed: {action.summary}</span>
      </div>
    );
  }

  if (status === 'cancelled') {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
        <X className="h-4 w-4 flex-shrink-0" />
        <span>Cancelled: {action.summary}</span>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
        <X className="h-4 w-4 flex-shrink-0" />
        <span>Failed: {action.summary}</span>
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
      <p className="text-sm font-semibold text-amber-900">Confirm this action?</p>
      <p className="text-sm text-amber-800">{action.summary}</p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleConfirm}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          Confirm
        </button>
        <button
          type="button"
          onClick={handleCancel}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          <X className="h-4 w-4" />
          Cancel
        </button>
      </div>
    </div>
  );
}
